/**
 * XPService — gamification events + canonical badge/XP granting for odv-truest.
 *
 * REWRITTEN 2026-07-08 (gseat/truest parity lane B):
 * The old version maintained a PHANTOM parallel XP store — it recomputed XP from a
 * bespoke formula (300/assignment + 30/guest) and wrote `users.xp` / `users.level`,
 * while the canonical XP truth is `valet_xp_ledger` + the `valets.total_xp`/`valets.level`
 * cache maintained by the `award_xp()` RPC (dedup + atomic; Feb 21 2026 unified system).
 * It also queried badge_definitions by non-existent `slug`/`xp_award` columns
 * (real: `badge_type`/`xp_value`), so its badge-XP math always returned 0.
 *
 * Canonical paths (same as gseat-pro — two dendrites, one node):
 *  - XP grant  → rpc('award_xp', { p_valet_id, p_amount, p_reason, p_source_id })
 *  - XP read   → valets.total_xp / valets.level (cache) or valet_xp_ledger (history)
 *  - Badge grant → awardBadgeWithXP below (schema-true badge_assignments insert with
 *    badge_definition_id + dedup; XP via award_xp). Discord sync + notifications rows
 *    are DB triggers on badge_assignments — never client-side.
 */

import { supabase } from '@/lib/supabase';

// Event emitter for gamification events
// Components can subscribe to these events to show toasts/celebrations
const gamificationEventListeners = {
  badgeEarned: [],
  levelUp: [],
  streakMilestone: [],
  xpGained: [],
};

export const GamificationEvents = {
  subscribe: (event, callback) => {
    if (gamificationEventListeners[event]) {
      gamificationEventListeners[event].push(callback);
      return () => {
        gamificationEventListeners[event] = gamificationEventListeners[event].filter(cb => cb !== callback);
      };
    }
  },
  emit: (event, data) => {
    if (gamificationEventListeners[event]) {
      gamificationEventListeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[GamificationEvents] Error in ${event} listener:`, error);
        }
      });
    }
  },
};

class XPService {
  /**
   * Resolve a valets.id from either a valets.id or an auth user id.
   * (auth_user_id ≠ valets.id — see memory feedback_auth-user-id-vs-valets-id.)
   */
  static async resolveValetId(subjectId) {
    if (!subjectId) return null;

    // Try auth_user_id first (ChallengeService passes auth user ids)
    const { data: byAuth } = await supabase
      .from('valets')
      .select('id')
      .eq('auth_user_id', subjectId)
      .maybeSingle();
    if (byAuth?.id) return byAuth.id;

    // Fall back to a direct valets.id
    const { data: byId } = await supabase
      .from('valets')
      .select('id')
      .eq('id', subjectId)
      .maybeSingle();
    return byId?.id || null;
  }

  /**
   * Award XP through the CANONICAL writer — award_xp() RPC.
   * Writes valet_xp_ledger, maintains valets.total_xp + valets.level, and dedups
   * on (sourceId, reason) when sourceId is a uuid.
   * @param {string} subjectId - valets.id or auth user id
   * @param {number} amount - XP to award (must be positive)
   * @param {string} reason - action label (becomes ledger source/action_type)
   * @param {string|null} sourceId - uuid of the causing entity (dedup key)
   */
  static async awardXP(subjectId, amount, reason, sourceId = null) {
    try {
      const valetId = await this.resolveValetId(subjectId);
      if (!valetId) {
        return { success: false, error: `No valet found for ${subjectId}` };
      }

      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const pSourceId =
        typeof sourceId === 'string' && UUID_RE.test(sourceId) ? sourceId : null;

      const { data, error } = await supabase.rpc('award_xp', {
        p_valet_id: valetId,
        p_amount: amount,
        p_reason: reason,
        p_source_id: pSourceId,
      });

      if (error) {
        console.error('[XPService] award_xp RPC failed:', error);
        return { success: false, error: error.message };
      }

      if (!data?.already_awarded) {
        GamificationEvents.emit('xpGained', {
          userId: subjectId,
          valetId,
          amount: data?.xp_awarded ?? amount,
          reason,
          newTotalXP: data?.new_total,
          newLevel: data?.new_level,
        });
      }

      return {
        success: true,
        alreadyAwarded: !!data?.already_awarded,
        xpAwarded: data?.xp_awarded ?? 0,
        newTotalXP: data?.new_total,
        newLevel: data?.new_level,
      };
    } catch (error) {
      console.error('[XPService] Error awarding XP:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Award a badge and its XP — the canonical truest badge grant.
   * Schema-true insert (badge_definition_id, valet_id, xp_earned) with dedup;
   * badge XP flows through award_xp deduped on the assignment id.
   * @param {string} subjectId - valets.id or auth user id
   * @param {string} badgeType - badge_definitions.badge_type
   * @param {Object} metadata - stored on assignment_context
   */
  static async awardBadgeWithXP(subjectId, badgeType, metadata = {}) {
    try {
      const valetId = await this.resolveValetId(subjectId);
      if (!valetId) {
        return { success: false, error: `No valet found for ${subjectId}` };
      }

      // Doctrine: never insert a badge_type with no badge_definitions row
      const { data: badgeDef, error: defError } = await supabase
        .from('badge_definitions')
        .select('id, badge_type, name, category, rarity, xp_value, is_active')
        .eq('badge_type', badgeType)
        .maybeSingle();

      if (defError || !badgeDef) {
        return { success: false, error: `Badge type '${badgeType}' has no badge_definitions row` };
      }
      if (badgeDef.is_active === false) {
        return { success: false, error: `Badge type '${badgeType}' is inactive` };
      }

      // Dedup on active (valet_id, badge_type)
      const { data: existing } = await supabase
        .from('badge_assignments')
        .select('id')
        .eq('valet_id', valetId)
        .eq('badge_type', badgeType)
        .eq('is_active', true)
        .maybeSingle();

      if (existing) {
        console.log(`[XPService] Badge ${badgeType} already active for valet ${valetId}`);
        return { success: true, alreadyAwarded: true, badge_type: badgeType };
      }

      // Award the badge (Discord sync + notification rows fire via DB triggers)
      const { data: badge, error: badgeError } = await supabase
        .from('badge_assignments')
        .insert([{
          valet_id: valetId,
          badge_type: badgeType,
          badge_definition_id: badgeDef.id,
          is_active: true,
          xp_earned: badgeDef.xp_value || 0,
          assigned_at: new Date().toISOString(),
          assignment_context: metadata,
        }])
        .select()
        .single();

      if (badgeError) {
        console.error('[XPService] Error awarding badge:', badgeError);
        return { success: false, error: badgeError.message };
      }

      // Badge XP through the canonical writer (deduped on the assignment id)
      let xpResult = { xpAwarded: 0 };
      if ((badgeDef.xp_value || 0) > 0) {
        xpResult = await this.awardXP(valetId, badgeDef.xp_value, `badge_${badgeType}`, badge.id);
      }

      GamificationEvents.emit('badgeEarned', {
        userId: subjectId,
        valetId,
        badgeName: badgeDef.name || badgeType,
        badgeType,
        category: badgeDef.category,
        xpAwarded: xpResult.xpAwarded || 0,
        rarity: badgeDef.rarity || 'common',
        newTotalXP: xpResult.newTotalXP,
        newLevel: xpResult.newLevel,
      });

      return {
        success: true,
        badge,
        badgeName: badgeDef.name,
        xpAwarded: xpResult.xpAwarded || 0,
        newTotalXP: xpResult.newTotalXP,
        newLevel: xpResult.newLevel,
      };
    } catch (error) {
      console.error('[XPService] Error awarding badge with XP:', error);
      return { success: false, error: error.message };
    }
  }
}

export default XPService;
